import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";

/**
 * OfferTrace transactional email send dialog.
 *
 * Semantics:
 * - Uses ONE stable client_request_id per submission attempt.
 *   If the network response is uncertain (timeout / network error),
 *   the user is shown a "Провери статуса" (check status) button and
 *   NOT a new "send again" button, to prevent duplicate sends across
 *   idempotency boundaries.
 * - Double-click submission is prevented by a ref-backed guard.
 * - Displays a test-mode banner when the backend config-status indicates
 *   recipient_override_active=true.
 * - When EMAIL_ENABLED=false the dialog surfaces a disabled notice and
 *   disables the send action.
 */
function uuidv4() {
  // RFC 4122-ish v4 UUID; not cryptographic — used only as a per-submission id.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function EmailSendDialog({
  open,
  quotation,
  sourceEmail, // optional: pre-fill from an existing email (resend)
  organizationName,
  fromEmail,
  fromDisplayName,
  replyToEmail,
  configStatus,
  onClose,
  onSent,
}) {
  const { t } = useTranslation();
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [uncertain, setUncertain] = useState(false);
  const clientRequestIdRef = useRef(null);
  const inflightRef = useRef(false);

  const enabled = !!configStatus?.enabled;
  const testMode = !!configStatus?.recipient_override_active;
  const isResend = !!sourceEmail;
  const orgName = organizationName || quotation?.organizations?.name || "";

  useEffect(() => {
    if (!open) return;
    // Initial values.
    const defaultRecipient =
      sourceEmail?.recipient_email ||
      quotation?.customer_email ||
      quotation?.customers?.email ||
      "";
    setRecipient(defaultRecipient);
    setSubject(
      sourceEmail?.subject ||
        t("quotations.email_default_subject", {
          reference: quotation?.reference_number || "",
          organization: orgName,
        }),
    );
    setMessage(
      sourceEmail?.message_text ||
        t("quotations.email_default_message", { sender: orgName }),
    );
    setConfirming(false);
    setSubmitting(false);
    setError("");
    setUncertain(false);
    clientRequestIdRef.current = uuidv4();
    inflightRef.current = false;
  }, [open, sourceEmail, quotation, orgName, t]);

  const canSubmit = useMemo(() => {
    if (!enabled) return false;
    if (submitting) return false;
    if (!recipient || !subject || !message) return false;
    if (recipient.includes(",") || recipient.includes(";")) return false;
    if (recipient.includes("\n") || recipient.includes("\r")) return false;
    if (subject.includes("\n") || subject.includes("\r")) return false;
    if (subject.length > 200) return false;
    if (message.length > 5000) return false;
    return true;
  }, [enabled, submitting, recipient, subject, message]);

  const doSend = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    setSubmitting(true);
    setError("");
    try {
      const cid = clientRequestIdRef.current;
      const path = isResend
        ? `/quotations/${quotation.id}/emails/${sourceEmail.id}/resend`
        : `/quotations/${quotation.id}/emails`;
      const res = await api.post(path, {
        recipient_email: recipient.trim(),
        subject: subject.trim(),
        message: message,
        client_request_id: cid,
      });
      if (typeof onSent === "function") onSent(res.data);
      onClose?.();
    } catch (err) {
      reportError("email.send", err);
      const detail = err?.response?.data?.detail;
      if (!err?.response) {
        // Network / timeout — uncertain outcome. Do NOT create a new
        // client_request_id automatically; offer status check.
        setUncertain(true);
        setError(t("quotations.email_send_error"));
      } else if (detail === "email_disabled" || detail === "email_not_configured") {
        setError(t("quotations.email_dialog_disabled_notice"));
      } else if (detail && typeof detail === "object" && typeof detail.message === "string") {
        // Structured project error: the server already supplies Bulgarian copy.
        setError(detail.message);
      } else if (detail && typeof detail === "string") {
        // A raw machine code must NEVER reach the user: an unmapped code falls
        // back to the generic Bulgarian sentence.
        setError(
          t(`quotations.email_failure_code.${detail}`, {
            defaultValue: t("quotations.email_send_error"),
          }),
        );
      } else {
        setError(t("quotations.email_send_error"));
      }
    } finally {
      setSubmitting(false);
      inflightRef.current = false;
    }
  }, [isResend, quotation, sourceEmail, recipient, subject, message, onClose, onSent, t]);

  const checkStatus = useCallback(async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await api.get(`/quotations/${quotation.id}/emails`);
      const list = res.data || [];
      // Look for the most recent row that matches recipient/subject.
      // We can't recompute the exact idempotency_key client-side; if the
      // record exists in list, we assume the send succeeded.
      const matched = list.find(
        (r) =>
          r.recipient_email === recipient.trim() &&
          r.subject === subject.trim(),
      );
      if (matched) {
        onSent?.(matched);
        onClose?.();
      } else {
        setError(t("quotations.email_send_error"));
      }
    } catch (err) {
      reportError("email.checkStatus", err);
      setError(t("quotations.email_send_error"));
    } finally {
      setSubmitting(false);
    }
  }, [quotation, recipient, subject, onSent, onClose, t]);

  if (!open) return null;

  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal qw-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-send-dialog-title"
        data-testid="email-send-dialog"
      >
        <div className="h" id="email-send-dialog-title">{t("quotations.email_dialog_title")}</div>
        <div className="body">
          {testMode && (
            <div
              data-testid="email-test-mode-banner"
              style={{
                background: "#fef3c7",
                color: "#92400e",
                padding: "10px 12px",
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 13,
                border: "1px solid #fde68a",
              }}
            >
              ⚠ {t("quotations.email_dialog_test_banner")}
            </div>
          )}
          {!enabled && (
            <div
              data-testid="email-disabled-notice"
              style={{
                background: "#f1f5f9",
                color: "#334155",
                padding: "10px 12px",
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 13,
              }}
            >
              {t("quotations.email_dialog_disabled_notice")}
            </div>
          )}

          <label htmlFor="email-recipient-input" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            {t("quotations.email_dialog_recipient")}
          </label>
          <input
            id="email-recipient-input"
            type="email"
            className="qw-input"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            data-testid="email-recipient-input"
            disabled={submitting || !enabled}
            style={{ width: "100%", marginBottom: 12 }}
          />

          <label htmlFor="email-subject-input" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            {t("quotations.email_dialog_subject")}
          </label>
          <input
            id="email-subject-input"
            type="text"
            className="qw-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            data-testid="email-subject-input"
            disabled={submitting || !enabled}
            style={{ width: "100%", marginBottom: 12 }}
          />

          <label htmlFor="email-message-input" style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            {t("quotations.email_dialog_message")}
          </label>
          <textarea
            id="email-message-input"
            className="qw-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            rows={7}
            data-testid="email-message-input"
            disabled={submitting || !enabled}
            style={{ width: "100%", marginBottom: 8, fontFamily: "inherit" }}
          />

          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
            <div>
              {t("quotations.email_dialog_from")}: {fromDisplayName || "OfferTrace"}
              {fromEmail ? ` <${fromEmail}>` : ""}
            </div>
            <div>
              {t("quotations.email_dialog_reply_to")}:{" "}
              {replyToEmail || t("quotations.email_dialog_reply_to_none")}
            </div>
            <div style={{ marginTop: 4 }}>{t("quotations.email_dialog_secure_link_note")}</div>
          </div>

          {confirming && (
            <div
              data-testid="email-confirm-summary"
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: 12,
                marginTop: 8,
                fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {t("quotations.email_dialog_confirm_summary")}
              </div>
              <div>
                {t("quotations.email_dialog_recipient")}: <strong>{recipient}</strong>
              </div>
              <div>
                {t("quotations.email_dialog_from")}:{" "}
                <strong>
                  {fromDisplayName || "OfferTrace"}
                  {fromEmail ? ` <${fromEmail}>` : ""}
                </strong>
              </div>
              <div>
                {t("quotations.email_dialog_subject")}: <strong>{subject}</strong>
              </div>
            </div>
          )}

          {error && (
            <div className="qw-error" style={{ marginTop: 8 }} data-testid="email-send-error">
              {error}
            </div>
          )}
        </div>
        <div className="footer">
          <button
            type="button"
            className="qw-btn ghost"
            onClick={onClose}
            data-testid="email-dialog-cancel"
            disabled={submitting}
          >
            {t("quotations.email_dialog_cancel")}
          </button>
          {uncertain ? (
            <button
              type="button"
              className="qw-btn primary"
              onClick={checkStatus}
              data-testid="email-check-status-btn"
              disabled={submitting}
            >
              {submitting ? t("common.loading") : t("quotations.email_check_status")}
            </button>
          ) : confirming ? (
            <button
              type="button"
              className="qw-btn primary"
              onClick={doSend}
              disabled={!canSubmit || submitting}
              data-testid="email-send-confirm-btn"
            >
              {submitting ? t("common.loading") : t("quotations.email_dialog_confirm")}
            </button>
          ) : (
            <button
              type="button"
              className="qw-btn primary"
              onClick={() => setConfirming(true)}
              disabled={!canSubmit}
              data-testid="email-send-review-btn"
            >
              {t("quotations.email_dialog_send")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
